import NextLink, { type LinkProps } from 'next/link'
import { Lock } from 'lucide-react'
import { highlightSafePro } from '@safe-global/views/components/common/ProHighlight'
import { Alert, AlertDescription, AlertTitle } from '@safe-global/views/components/ui/alert'
import { Button } from '@safe-global/views/components/ui/button'
import { Typography } from '@safe-global/views/components/ui/typography'
import {
  MixpanelEventParams,
  PlanSelectionEntryPoint,
  UpgradeLocation,
  type UpgradeFeature,
} from '@/services/analytics'
import { useTrackOnce } from '@/services/analytics/useTrackOnce'
import { SAFE_PRO_EVENTS } from '@/services/analytics/events/safe-pro'
import { trackPlanSelectionStarted } from '@/features/spaces'

/** Safe Pro upsell replacing an add-policy action (proposer, spending limit) when `usePlanGate` requires an upgrade. */
const SafeProLock = ({ title, href, feature }: { title: string; href: LinkProps['href']; feature: UpgradeFeature }) => {
  const prompt = {
    [MixpanelEventParams.FEATURE]: feature,
    [MixpanelEventParams.LOCATION]: UpgradeLocation.SETTINGS_SETUP,
  }
  useTrackOnce(SAFE_PRO_EVENTS.UPGRADE_PROMPT_VIEWED, prompt)

  return (
    <Alert variant="subtle" data-testid="safe-pro-lock">
      <Lock />
      <AlertTitle>
        <Typography variant="paragraph-bold">{highlightSafePro(title)}</Typography>
      </AlertTitle>
      <AlertDescription>
        <Typography>Existing ones stay active.</Typography>
        <Button
          size="action"
          render={<NextLink href={href} />}
          onClick={() =>
            trackPlanSelectionStarted({
              [MixpanelEventParams.ENTRY_POINT]: PlanSelectionEntryPoint.UPGRADE_PROMPT,
              ...prompt,
            })
          }
        >
          Explore Safe Pro
        </Button>
      </AlertDescription>
    </Alert>
  )
}

export default SafeProLock
